import React from 'react'
import type { CourseDto, CourseApiDto, IconType } from '../types'
import CarIcon from '../assets/icons/carWithGradient.svg'
import DrivingIcon from '../assets/icons/driving.svg'
import PoliceIcon from '../assets/icons/police.svg'
import MotorcycleIcon from '../assets/icons/motorcycle.svg'
import { normalizePlainText, normalizeRichText } from '../utils/content'

// Icon type from imports (can be string URL or React component depending on bundler config)
type IconImport = typeof CarIcon

// Icon mapping for converting API icon_type to actual icon
const iconMap: Record<Exclude<IconType, 'custom'>, IconImport> = {
    car: CarIcon,
    motorcycle: MotorcycleIcon,
    driving: DrivingIcon,
    police: PoliceIcon,
}

// Helper function to get the icon based on icon_type and custom_image_url
export const getIconForCourse = (iconType: IconType, customImageUrl: string | null): IconImport | string => {
    if (iconType === 'custom' && customImageUrl) {
        return customImageUrl
    }
    return iconMap[iconType as Exclude<IconType, 'custom'>] || CarIcon
}

// Helper function to convert API course data to CourseDto
export const convertApiCourseToCourseDto = (apiCourse: CourseApiDto): CourseDto => {
    return {
        title: apiCourse.title,
        subtitle: apiCourse.subtitle ?? undefined,
        subSubtitle: apiCourse.sub_subtitle ?? undefined,
        duration: apiCourse.duration ?? undefined,
        price: apiCourse.price,
        shortDescription: normalizePlainText(apiCourse.short_description),
        // Render HTML description using dangerouslySetInnerHTML wrapper
        description: <div className='rich-content' dangerouslySetInnerHTML={{ __html: normalizeRichText(apiCourse.description) }} />,
        image: getIconForCourse(apiCourse.icon_type, apiCourse.custom_image_url),
        smallText: Number(apiCourse.small_text) === 1,
    }
}

// Helper function to convert array of API courses to CourseDto array
export const convertApiCoursesToCourseDtos = (apiCourses: CourseApiDto[]): CourseDto[] => {
    return apiCourses.map(convertApiCourseToCourseDto)
}

// Fallback courses (the original hardcoded data) - used when API fails or while loading
export const fallbackCourses: CourseDto[] = [
    {
        title: 'SKUPINA',
        subtitle: 'B',
        subSubtitle: 'Klasik',
        duration: '2 až 3 měsíce',
        price: '20 500,- Kč',
        shortDescription:
            'Opravňuje k řízení motorových vozidel do celkové hmotnosti 3,5 tuny a max. počtu 8 cestujících plus řidič',
        description: (
            <>
                <h3 className='text-xl font-semibold mb-3'>Typ řidičského průkazu: Skupina B</h3>
                <div className='mb-3'>
                    <h4 className='font-semibold'>Co řidičský průkaz skupiny B umožňuje:</h4>
                    <p>
                        Opravňuje k řízení motorových vozidel do celkové hmotnosti 3,5 tuny s nejvýše 8 cestujícími plus řidič a s
                        nebrzděným přívěsem do 750 kg. Dále umožňuje řídit motocykly s automatickou převodovkou a zdvihovým
                        objemem do 125 m3.
                    </p>
                </div>

                <div className='mb-3'>
                    <h4 className='font-semibold'>Délka výcviku:</h4>
                    <p>Obvykle 2 až 3 měsíce, záleží na časových možnostech žadatele.</p>
                </div>

                <div className='mb-3'>
                    <h4 className='font-semibold'>Zkouška:</h4>
                    <p>Zkouška se skládá ze dvou částí:</p>
                    <ul className='list-disc pl-5 text-left'>
                        <li>teoretické zkoušky (test s 25 otázkami o předpisech o provozu na pozemních komunikacích)</li>
                        <li>praktické zkoušky (minimálně 30 minut jízdy v běžném městském provozu)</li>
                    </ul>
                    <p>
                        Žadatel musí mít platný občanský průkaz a skládá zkoušku na Magistrátu města Chomutova. Poplatek magistátu
                        za připuštění ke zkoušce je 700 Kč, za opakování písemného testu 100 Kč, za opakování praktické zkoušky je
                        400 Kč. Poplatek autoškole za každé opakování zkoušky činí 300,- Kč.
                    </p>
                </div>

                <div className='mb-3'>
                    <h4 className='font-semibold'>Teoretická část výcviku v délce 36 hodin zahrnuje:</h4>
                    <ul className='list-disc pl-5 text-left'>
                        <li>pravidla o provozu vozidel na pozemních komunikacích</li>
                        <li>ovládání a údržbu vozidla</li>
                        <li>zásady bezpečné jízdy a teorii jízdy</li>
                        <li>zdravotní přípravu</li>
                        <li>opakování a přezkoušení</li>
                    </ul>
                </div>

                <div className='mb-3'>
                    <h4 className='font-semibold'>Praktická část výcviku v délce 34 hodin zahrnuje:</h4>
                    <ul className='list-disc pl-5 text-left'>
                        <li>řízení vozidla ve 3 etapách - za nízkého, středního, městského provozu</li>
                        <li>praktickou údržbu vozidla</li>
                    </ul>
                </div>

                <p className='mt-4 font-semibold'>
                    Za zrušení jízdy méně než 24 hodin předem účtujeme poplatek 100,-Kč, při zrušení hodinu a méně před jízdou
                    účtujeme 200,-Kč.
                </p>
            </>
        ),
        image: CarIcon,
    },
    {
        title: 'SKUPINA',
        subtitle: 'B',
        subSubtitle: 'Automat',
        duration: '2 až 3 měsíce',
        price: '20 500,- Kč',
        shortDescription:
            'Opravňuje pouze k řízení motorových vozidel s automatickou převodovkou do celkové hmotnosti 3,5 tuny a max. počtu 8 cestujících plus řidič',
        description: (
            <>
                <h3 className='text-xl font-semibold mb-3'>Typ řidičského průkazu: Skupina B</h3>
                <div className='mb-3'>
                    <h4 className='font-semibold'>Co řidičský průkaz skupiny B umožňuje:</h4>
                    <p>
                        Opravňuje k řízení pouze motorových vozidel z automatickou převodovkou do celkové hmotnosti 3,5 tuny s
                        nejvýše 8 cestujícími plus řidič a s nebrzděným přívěsem do 750 kg. Dále umožňuje řídit motocykly s
                        automatickou převodovkou a zdvihovým objemem do 125 m3.
                    </p>
                </div>

                <div className='mb-3'>
                    <h4 className='font-semibold'>Délka výcviku:</h4>
                    <p>Obvykle 2 až 3 měsíce, záleží na časových možnostech žadatele.</p>
                </div>

                <div className='mb-3'>
                    <h4 className='font-semibold'>Zkouška:</h4>
                    <p>Zkouška se skládá ze dvou částí:</p>
                    <ul className='list-disc pl-5 text-left'>
                        <li>teoretické zkoušky (test s 25 otázkami o předpisech o provozu na pozemních komunikacích)</li>
                        <li>praktické zkoušky (minimálně 30 minut jízdy v běžném městském provozu)</li>
                    </ul>
                    <p>
                        Žadatel musí mít platný občanský průkaz a skládá zkoušku na Magistrátu města Chomutova. Poplatek autoškole
                        za každé opakování zkoušky činí 300,- Kč.
                    </p>
                </div>

                <div className='mb-3'>
                    <h4 className='font-semibold'>Teoretická část výcviku v délce 36 hodin zahrnuje:</h4>
                    <ul className='list-disc pl-5 text-left'>
                        <li>pravidla o provozu vozidel na pozemních komunikacích</li>
                        <li>ovládání a údržbu vozidla</li>
                        <li>zásady bezpečné jízdy a teorii jízdy</li>
                        <li>zdravotní přípravu</li>
                        <li>opakování a přezkoušení</li>
                    </ul>
                </div>

                <div className='mb-3'>
                    <h4 className='font-semibold'>Praktická část výcviku v délce 34 hodin zahrnuje:</h4>
                    <ul className='list-disc pl-5 text-left'>
                        <li>řízení vozidla ve 3 etapách - za nízkého, středního, městského provozu</li>
                        <li>praktickou údržbu vozidla</li>
                    </ul>
                </div>

                <p className='mt-4 font-semibold'>
                    Za zrušení jízdy méně než 24 hodin předem účtujeme poplatek 100,-Kč, při zrušení hodinu a méně před jízdou
                    účtujeme 200,-Kč.
                </p>
            </>
        ),
        image: CarIcon,
    },
    {
        title: 'SKUPINA',
        subtitle: 'B',
        subSubtitle: 'Rychlokurz',
        duration: '4 až 5 týdnů',
        price: '30 000,- Kč',
        shortDescription:
            'Opravňuje k řízení motorových vozidel do celkové hmotnosti 3,5 tuny a max. počtu 8 cestujících plus řidič',
        description: (
            <>
                <h3 className='text-xl font-semibold mb-3'>Typ řidičského průkazu: Skupina B</h3>
                <div className='mb-3'>
                    <h4 className='font-semibold'>Co řidičský průkaz skupiny B umožňuje:</h4>
                    <p>
                        Opravňuje k řízení motorových vozidel do celkové hmotnosti 3,5 tuny s nejvýše 8 cestujícími plus řidič a s
                        nebrzděným přívěsem do 750 kg. Dále umožňuje řídit motocykly s automatickou převodovkou a zdvihovým
                        objemem do 125 m3.
                    </p>
                </div>

                <div className='mb-3'>
                    <h4 className='font-semibold'>Délka výcviku:</h4>
                    <p>Obvykle 4 až 5 týdnů, záleží na časových možnostech žadatele.</p>
                </div>

                <div className='mb-3'>
                    <h4 className='font-semibold'>Zkouška:</h4>
                    <p>Zkouška se skládá ze dvou částí:</p>
                    <ul className='list-disc pl-5 text-left'>
                        <li>teoretické zkoušky (test s 25 otázkami o předpisech o provozu na pozemních komunikacích)</li>
                        <li>praktické zkoušky (minimálně 30 minut jízdy v běžném městském provozu)</li>
                    </ul>
                    <p>
                        Žadatel musí mít platný občanský průkaz a skládá zkoušku na Magistrátu města Chomutova. Poplatek magistátu
                        za připuštění ke zkoušce je 700 Kč, za opakování písemného testu 100 Kč, za opakování praktické zkoušky je
                        400 Kč. Poplatek autoškole za každé opakování zkoušky činí 300,- Kč.
                    </p>
                </div>

                <div className='mb-3'>
                    <h4 className='font-semibold'>Teoretická část výcviku v délce 36 hodin zahrnuje:</h4>
                    <ul className='list-disc pl-5 text-left'>
                        <li>pravidla o provozu vozidel na pozemních komunikacích</li>
                        <li>ovládání a údržbu vozidla</li>
                        <li>zásady bezpečné jízdy a teorii jízdy</li>
                        <li>zdravotní přípravu</li>
                        <li>opakování a přezkoušení</li>
                    </ul>
                </div>

                <div className='mb-3'>
                    <h4 className='font-semibold'>Praktická část výcviku v délce 34 hodin zahrnuje:</h4>
                    <ul className='list-disc pl-5 text-left'>
                        <li>řízení vozidla ve 3 etapách - za nízkého, středního, městského provozu</li>
                        <li>praktickou údržbu vozidla</li>
                    </ul>
                </div>

                <p className='mt-4 font-semibold'>
                    Za zrušení jízdy méně než 24 hodin předem účtujeme poplatek 100,-Kč, při zrušení hodinu a méně před jízdou
                    účtujeme 200,-Kč.
                </p>
            </>
        ),
        image: CarIcon,
    },
    {
        title: 'SKUPINA',
        subtitle: 'B+E',
        subSubtitle: 'Jízdní souprava',
        price: '6 500,- Kč',
        shortDescription:
            'Opravňuje k řízení jízdní soupravy tvořené motorovým vozidlem skupiny B a přípojným vozidlem o největší povolené hmotnosti vyšší než 750 kg',
        description: (
            <>
                <h3 className='text-xl font-semibold mb-3'>Jízdní souprava</h3>

                <div className='mb-4'>
                    <h4 className='font-semibold'>Teorie - 14 hodin:</h4>
                    <ul className='list-disc pl-5 text-left'>
                        <li>výuka předpisů o provozu vozidel</li>
                        <li>ovládání a údržba vozidla</li>
                        <li>výuka teorie řízení a zásad bezpečné jízdy</li>
                        <li>zdravověda</li>
                        <li>opakování</li>
                    </ul>
                </div>

                <div className='mb-3'>
                    <h4 className='font-semibold'>Praxe - 8 hodin:</h4>
                    <ul className='list-disc pl-5 text-left'>
                        <li>výcvik v řízení vozidla</li>
                    </ul>
                </div>
            </>
        ),
        image: CarIcon,
    },
    {
        title: 'SKUPINA',
        subtitle: 'A1',
        subSubtitle: 'Motocykl do 125 cm3',
        duration: 'Možné vždy v období od března do října s ohledem na počasí',
        price: '15 000,- Kč',
        shortDescription:
            'Opravňuje k řízení lehkého motocyklu do výkonu 11 kW a výkon/hmotnosti 0,1 kW/kg a se zdvihovým objemem spalovacího motoru nepřevyšujícím 125 cm3.',
        description: (
            <>
                <h3 className='text-xl font-semibold mb-3'>Motocykl do 125 cm3</h3>
                <p className='mb-3'>
                    Opravňuje k řízení lehkého motocyklu s postranním vozíkem nebo bez něj do výkonu 11 kW a s poměrem
                    výkon/hmotnosti 0,1 kW/kg a se zdvihovým objemem spalovacího motoru nepřevyšujícím 125 cm3.
                </p>

                <div className='mb-4'>
                    <p>
                        <span className='font-semibold'>Minimální věk:</span> 16 let v den ukončení výcviku
                    </p>
                    <p>
                        <span className='font-semibold'>Výcvikové vozidlo:</span> KTM 125 Duke
                    </p>
                </div>

                <div className='mb-4'>
                    <h4 className='font-semibold'>Teorie - 26 hodin:</h4>
                    <ul className='list-disc pl-5 text-left'>
                        <li>výuka předpisů o provozu vozidel</li>
                        <li>výuka o ovládání a údržbě vozidla</li>
                        <li>výuka teorie řízení a zásad bezpečné jízdy</li>
                        <li>výuka zdravotnické přípravy</li>
                        <li>opakování a přezkoušení</li>
                    </ul>
                </div>

                <div className='mb-4'>
                    <h4 className='font-semibold'>Praxe - 18 hodin:</h4>
                    <ul className='list-disc pl-5 text-left'>
                        <li>výcvik praktické údržby</li>
                        <li>praktický výcvik zdravotnické přípravy</li>
                        <li>výcvik v řízení vozidla</li>
                    </ul>
                </div>

                <div className='mt-4 border-t border-gray-600 pt-3'>
                    <h4 className='font-semibold'>Rozšíření z B:</h4>
                    <p>
                        <span className='font-semibold'>Teorie:</span> 16 hodin
                    </p>
                    <p>
                        <span className='font-semibold'>Praxe:</span> 16 hodin
                    </p>
                </div>
            </>
        ),
        image: MotorcycleIcon,
        smallText: true,
    },
    {
        title: 'SKUPINA',
        subtitle: 'A2',
        subSubtitle: 'Motocykl do 35kW',
        duration: 'Možné vždy v období od března do října s ohledem na počasí',
        price: '15 000,- Kč',
        shortDescription:
            'Opravňuje k řízení motocyklu do výkonu 35 kW a s poměrem výkon/hmotnost nejvýše 0,2 kW/kg, které nebyly upraveny z motocyklu s více než dvojnásobným výkonem.',
        description: (
            <>
                <h3 className='text-xl font-semibold mb-3'>Motocykl do 35kW</h3>
                <p className='mb-3'>
                    Po dosažení 18 let opravňuje k řízení motocyklu do výkonu 35 kW a s poměrem výkon/hmotnost nejvýše 0,2 kW/kg,
                    které nebyly upraveny z motocyklu s více než dvojnásobným výkonem.
                </p>

                <div className='mb-4'>
                    <p>
                        <span className='font-semibold'>Minimální věk:</span> 18 let v den ukončení výcviku
                    </p>
                </div>

                <div className='mb-4'>
                    <h4 className='font-semibold'>Teorie - 26 hodin:</h4>
                    <ul className='list-disc pl-5 text-left'>
                        <li>výuka předpisů o provozu vozidel</li>
                        <li>výuka o ovládání a údržbě vozidla</li>
                        <li>výuka teorie řízení a zásad bezpečné jízdy</li>
                        <li>výuka zdravotnické přípravy</li>
                        <li>opakování a přezkoušení</li>
                    </ul>
                </div>

                <div className='mb-4'>
                    <h4 className='font-semibold'>Praxe - 14 hodin:</h4>
                    <ul className='list-disc pl-5 text-left'>
                        <li>výcvik v řízení vozidla (13 hodin)</li>
                        <li>výcvik praktické údržby (1 hodina)</li>
                    </ul>
                </div>

                <div className='mt-4 border-t border-gray-600 pt-3'>
                    <h4 className='font-semibold'>Rozšíření z B:</h4>
                    <p>
                        <span className='font-semibold'>Teorie:</span> 16 hodin
                    </p>
                    <p>
                        <span className='font-semibold'>Praxe:</span> 16 hodin
                    </p>
                </div>
            </>
        ),
        image: MotorcycleIcon,
        smallText: true,
    },
    {
        title: 'SKUPINA',
        subtitle: 'A',
        subSubtitle: 'Motocykl bez omezení',
        duration: 'Možné vždy v období od března do října s ohledem na počasí',
        price: '15 000,- Kč',
        shortDescription:
            'Opravňuje k řízení motocyklu s postranním vozíkem nebo bez něj a tříkolová motorová vozidla s výkonem převyšujícím 15 kW. Výcvikové vozidlo Honda CB750 Hornet.',
        description: (
            <>
                <h3 className='text-xl font-semibold mb-3'>Motocykl bez omezení</h3>
                <p className='mb-3'>
                    Opravňuje k řízení motocyklu s postranním vozíkem nebo bez něj a tříkolová motorová vozidla s výkonem
                    převyšujícím 15 kW.
                </p>

                <div className='mb-4'>
                    <p>
                        <span className='font-semibold'>Minimální věk:</span> 24 let v den ukončení výcviku
                    </p>
                    <p>
                        <span className='font-semibold'>Výcvikové vozidlo:</span> Honda CB750 Hornet
                    </p>
                </div>

                <div className='mb-4'>
                    <h4 className='font-semibold'>Teorie - 26 hodin:</h4>
                    <ul className='list-disc pl-5 text-left'>
                        <li>výuka předpisů o provozu vozidel</li>
                        <li>výuka o ovládání a údržbě vozidla</li>
                        <li>výuka teorie řízení a zásad bezpečné jízdy</li>
                        <li>výuka zdravotnické přípravy</li>
                        <li>opakování a přezkoušení</li>
                    </ul>
                </div>

                <div className='mb-4'>
                    <h4 className='font-semibold'>Praxe - 18 hodin:</h4>
                    <ul className='list-disc pl-5 text-left'>
                        <li>výcvik v řízení vozidla</li>
                        <li>výcvik praktické údržby</li>
                        <li>praktický výcvik zdravotnické přípravy</li>
                    </ul>
                </div>

                <div className='mt-4 border-t border-gray-600 pt-3'>
                    <h4 className='font-semibold'>Rozšíření z B:</h4>
                    <p>
                        <span className='font-semibold'>Teorie:</span> 16 hodin
                    </p>
                    <p>
                        <span className='font-semibold'>Praxe:</span> 16 hodin
                    </p>
                </div>
            </>
        ),
        image: MotorcycleIcon,
    },
    {
        title: 'KONDIČNÍ JÍZDY',
        subSubtitle: 'Skupina B',
        duration: '45 min',
        price: '500,- Kč za 45 minut',
        shortDescription:
            'Potřebujete-li si zdokonalit řidičské schopnosti nebo jste delší dobu neřídili, nabízíme kondiční hodiny pod dohledem instruktora.',
        description: (
            <>
                <h3 className='text-xl font-semibold mb-3'>Kondiční jízdy - Skupina B</h3>
                <p className='mb-3'>
                    Potřebujete-li si zdokonalit řidičské schopnosti nebo jste delší dobu neřídili, nabízíme kondiční hodiny v
                    délce 45 minut pod dohledem instruktora.
                </p>

                <div className='mb-4'>
                    <p className='mb-2'>
                        Podmínkou je aktuální účast ve výcviku nebo vlastnictví příslušného řidičského oprávnění.
                    </p>
                    <p>Počet kondičních jízd a typ trasy si vyberete podle Vašich potřeb.</p>
                </div>

                <p className='mt-4 text-lg font-semibold'>Cena za 45 minut je 500,- Kč.</p>
            </>
        ),
        image: DrivingIcon,
    },
    {
        title: 'Přezkoušení z odborné způsobilosti',
        price: '6 000,- Kč',
        shortDescription:
            'Vrácení řidičského průkazu sk. B po vybodování, po odebrání ze zdravotních důvodů nebo jiného uloženého zákazu řízení.',
        description: (
            <>
                <h3 className='text-xl font-semibold mb-3'>Přezkoušení z odborné způsobilosti</h3>
                <p className='mb-3'>
                    Vrácení řidičského průkazu sk. B po vybodování, po odebrání ze zdravotních důvodů nebo jiného uloženého zákazu
                    řízení.
                </p>

                <div className='mb-4'>
                    <p>
                        Potřebujete-li získat zpět řidičské oprávnění sk. B, nabízíme Vám možnost přípravy a zajištění přezkoušení
                        včetně veškeré administrativy spojené se zkouškou.
                    </p>
                </div>
            </>
        ),
        image: PoliceIcon,
    },
]
